import _ from 'lodash'
import type { Hive } from '../../src/modules/hive'
import { checkMeta } from '../../src/utils/sync'
import { IDB_locationTimelineEventsWrite } from '../IDB/IDB.table.locationTimelineEvent'
import { Serv_send } from '../Serv/Serv.socketClient'
import type { Type_ILocationTimelineEvent } from '../Type/Type.hive'
import { Util_unixTime } from '../Util/Util.date'

export const Loca_isRedundant = checkMeta('redundant')

export async function Loca_fetchTimelineEvents(
    hive: Hive,
    timelines: Type_ILocationTimelineEvent[],
    locationIds: number[],
    updateTimeline: () => void
) {
    const allLocationIds = _.uniq(
        _.concat(
            timelines.map((entry) => entry.id),
            locationIds
        )
    )

    const currentDate = new Date()

    // Fetch timelines for all locations from the server
    const sendData = {
        spec: {
            from: currentDate.toISOString(),
            since: hive.getlastDBupdate('timeline').value,
        },
        locations: allLocationIds,
    }

    // Retrieve the timeline events from the server
    const result = await Serv_send('v2.timeline.getAll', sendData)

    // Set the last db update time
    const time = currentDate.getTime()
    hive.setlastDBupdate('timeline', Util_unixTime(time))

    // Convert the retrieved events into the format expected by the app
    const updatedTimelineEvents: Type_ILocationTimelineEvent[] = result.timelines
        .map((timeline) => ({
            id: timeline.location,
            events: timeline.events.map((event) => {
                if (event.type === 'smartdata') {
                    // TODO The smartdata type needs and id but we're not sure what
                    // it should be and the server doesn't send it
                    return {
                        id: event.id,
                        type: 'smartdata' as const,
                        item: { ...event.item, id: event.id },
                        date: event.date,
                    }
                }

                return event
            }),
        }))
        // Merge the new events into the old events we have in the local database
        .map((newTimeline) => {
            // Find the old events for this location
            const oldState = timelines.find((oldTimeline) => oldTimeline.id === newTimeline.id)

            const oldEvents = ((oldState && oldState.events) || [])
                // Filter out local 'redundant' events as they were temporary events that are no longer needed
                .filter((event) => !Loca_isRedundant(event))

            // Merge old events and new, unique them by ID and sort by date
            return {
                id: newTimeline.id,
                previousTimeline: oldState,
                events: _.reverse(_.sortBy(_.uniqBy(_.concat(oldEvents, newTimeline.events), 'id'), ['date'])),
            }
        })
        // Filter out entries that haven't changed to avoid doing too many updates on the locations table.
        // Otherwise there's an update per location every sync.
        .filter((timeline) => {
            const { previousTimeline } = timeline

            // Keep if there is no previous timeline for this location as all information must be new.
            if (previousTimeline === undefined) {
                return true
            }

            // Keep if there are a different number of events between old and new timelines
            const previousTimelineEventCount = previousTimeline.events.length
            const newTimelineEventCount = timeline.events.length

            if (previousTimelineEventCount !== newTimelineEventCount) {
                return true
            }

            // Update if the latest event is different even if there is the same number of events
            if (previousTimelineEventCount > 0 && newTimelineEventCount > 0) {
                const newTimelineLatestEventTime = new Date(timeline.events[0].date).getTime()
                const previousTimelineLatestEventTime = new Date(previousTimeline.events[0].date).getTime()

                return previousTimelineLatestEventTime !== newTimelineLatestEventTime
            }

            // Don't update any timelines that don't meet this requirement
            return false
        })
        // Remap to the correct format (remove previousTimeline)
        .map((timeline) => ({ id: timeline.id, events: timeline.events }))

    // Add all timelines to the local database
    if (updatedTimelineEvents.length > 0) {
        await IDB_locationTimelineEventsWrite(updatedTimelineEvents)
        updateTimeline()
    }
}
